import clsx from "clsx"
import { useState } from "react"

import useAppHotkey from "@hooks/useAppHotkey"
import useIsDesktop from "@hooks/useIsDesktop"
import useRouter from "@hooks/useRouter"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import replaceSlugs from "@utils/replaceSlugs"

import { HotKey, Icon } from "@atoms"

import { HotKeyStack } from "@molecules/index"

import SearchModal from "@organisms/SideBar/MainSideBar/SideBarSearchButton/SearchModal/SearchModal"

import { SEARCH_ROUTE } from "@routes/search"

import styles from "./SideBarSearchButton.module.scss"
import { SideBarSearchButtonProps } from "./SideBarSearchButton.props"

const SEARCH_KEYBOARD_HOTKEY = "Mod+K"

export default function SideBarSearchButton(props: SideBarSearchButtonProps) {
    const { isDisabled = false, _isFocused = false, _isHovered = false, isPressed = false } = props

    const { user } = useUser()

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const isDesktop = useIsDesktop()

    const router = useRouter()

    const onSearchButtonClick = () => {
        if (isDisabled) {
            return null
        } else if (isDesktop) {
            setIsOpen(!isOpen)
        } else {
            router.push(
                replaceSlugs(SEARCH_ROUTE, {
                    service_company_slug: user?.service_company?.slug ?? "undefined",
                }),
            )
        }
    }

    useAppHotkey(
        SEARCH_KEYBOARD_HOTKEY,
        (event) => {
            if (isDisabled) {
                return null
            }
            // Prevent the browser's search from opening
            event.preventDefault()
            onSearchButtonClick()
        },
        {
            // Allow Cmd+K even in form fields since it's a universal search shortcut
            enableOnFormTags: true,
            enabled: !isDisabled && isDesktop,
        },
        [isOpen, isDisabled, isDesktop],
    )

    return (
        <>
            <button
                className={clsx(styles.base, {
                    [styles.focused]: _isFocused,
                    [styles.hovered]: _isHovered,
                    [styles.pressed]: isPressed,
                    [styles.disabled]: isDisabled,
                })}
                onClick={onSearchButtonClick}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                aria-pressed={isPressed}
                tabIndex={isDisabled ? -1 : 0}
            >
                <div className={styles.iconAndText}>
                    <Icon
                        className={styles.icon}
                        icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                        size={14}
                    />
                    <span className={styles.label}>Search anything</span>
                </div>
                {!isDisabled && isDesktop && (
                    <HotKeyStack className={styles.hotKeyStack}>
                        <HotKey symbolName="Mod" />
                        <HotKey symbolName="K" />
                    </HotKeyStack>
                )}
            </button>
            <SearchModal isOpen={isOpen} onOpenChange={setIsOpen} />
        </>
    )
}
